import React, { useMemo } from "react";
import { Home, MessageCircle, SlidersHorizontal } from "lucide-react";
import { fmtOdd } from "../../utils/bettingDeskHelpers";
import StartingSoonHeaderTicker from "./StartingSoonHeaderTicker";
import AppLiveClock from "../ui/AppLiveClock";

const houseInitials = (name = "") => {
  const trimmed = name.trim();
  if (!trimmed) return "??";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const PlaceBetDeskChrome = ({
  houseName,
  liveCount = 0,
  startingSoonGroups = [],
  startingSoon = false,
  onStartingSoonToggle,
  openCount = 0,
  slipCount = 0,
  combinedOdds = 0,
  onViewHouse,
  onChat,
  filtersOpen,
  onFiltersToggle,
  activeFilterCount = 0,
  children,
}) => {
  const initials = useMemo(() => houseInitials(houseName), [houseName]);
  const hasSoonTicker = startingSoonGroups.length > 0;
  const balanced = Boolean(children && hasSoonTicker);

  return (
    <header className={`pbd-chrome ${children ? "pbd-chrome-has-toolbar" : ""}`}>
      <div className={`pbd-chrome-row${balanced ? " pbd-chrome-row-balanced" : ""}`}>
        <div className="pbd-chrome-cluster pbd-chrome-cluster-start">
          <div className="pbd-chrome-main">
            <div className="pbd-chrome-house">
              <span className="pbd-chrome-house-mark" aria-hidden>
                {initials}
              </span>
              <div className="pbd-chrome-house-copy">
                <p className="pbd-chrome-house-eyebrow">Bet desk</p>
                <h1 className="pbd-chrome-house-name bondhouse-name truncate" title={houseName}>
                  {houseName || "Bet house"}
                </h1>
              </div>
              {liveCount > 0 ? (
                <span
                  className="pbd-chrome-live pbd-chrome-live-house"
                  aria-label={`${liveCount} live fixtures`}
                >
                  <span className="pbd-chrome-live-dot" aria-hidden />
                  <span className="tabular-nums">{liveCount}</span>
                </span>
              ) : null}
            </div>
            {!children ? (
              <div className="pbd-chrome-meta" aria-label="Desk summary">
                <span className="tabular-nums">{openCount} open</span>
                <span className="tabular-nums">{slipCount} on slip</span>
                {slipCount > 0 ? (
                  <span className="pbd-chrome-meta-odds tabular-nums">×{fmtOdd(combinedOdds)}</span>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        {balanced ? (
          <div className="pbd-chrome-cluster pbd-chrome-cluster-center">
            <StartingSoonHeaderTicker
              groups={startingSoonGroups}
              active={startingSoon}
              onToggle={onStartingSoonToggle}
            />
          </div>
        ) : (
          <div className="pbd-chrome-center app-chrome-clock-center">
            <AppLiveClock className="app-chrome-clock" />
          </div>
        )}

        <div className="pbd-chrome-cluster pbd-chrome-cluster-end">
          {onFiltersToggle ? (
            <button
              type="button"
              className={`pbd-chrome-btn pbd-chrome-btn-filters${
                filtersOpen || activeFilterCount ? " pbd-chrome-btn-filters-active" : ""
              }`}
              onClick={onFiltersToggle}
              aria-label="Program filters"
              title="Filters"
            >
              <SlidersHorizontal size={14} strokeWidth={2.3} />
              {activeFilterCount ? (
                <span className="pbd-chrome-filter-badge">{activeFilterCount}</span>
              ) : null}
            </button>
          ) : null}
          <div className="pbd-chrome-actions">
            {children}
            <button
              type="button"
              className="pbd-chrome-btn"
              onClick={onViewHouse}
              aria-label="View house"
            >
              <Home size={14} strokeWidth={2.3} />
            </button>
            <button
              type="button"
              className="pbd-chrome-btn"
              onClick={onChat}
              aria-label="Open chat"
            >
              <MessageCircle size={14} strokeWidth={2.3} />
            </button>
          </div>
        </div>
      </div>

      {!children && hasSoonTicker ? (
        <div className="pbd-chrome-soon-row">
          <StartingSoonHeaderTicker
            groups={startingSoonGroups}
            active={startingSoon}
            onToggle={onStartingSoonToggle}
          />
        </div>
      ) : null}
    </header>
  );
};

export default PlaceBetDeskChrome;
